import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { OnboardingAction } from 'fym-shared/src/music24/onboardingChecklist';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { useArtistSwitcher } from '../../accountSelector/useArtistSwitcher';
import { firstOwnAccountId } from '../../accountSelector/demoAccounts';
import { catalogSongPath } from '../../smartLinks/catalog/song/page/catalogSongPath';
import { releaseThemeEditPath } from '../../music24/promote/releases/releasePageLink';

/**
 * Runs the button of a checklist item (design: onboarding d1, d6). The server names the screen
 * and the artist (Ruling R14); this hook maps it to a web route. A song action passes the artist
 * of the checklist as `accountId`; the hook selects that artist first when another artist (or the
 * demo artist) is active, so Channels and the Catalog open for the right artist. With the demo
 * active and no artist in the action, it selects the first own artist.
 */
export function useChecklistAction(): (action: OnboardingAction) => void {
  const dispatch = useDispatch();
  const router = useSMLRouter();
  const navigate = useNavigate();
  const { accounts, active, onSelect } = useArtistSwitcher();
  const isDemoActive = active?.isDemo === true;

  return useCallback(
    (action: OnboardingAction) => {
      if (action.screen === 'plans') {
        dispatch(toggleSMLPricingModal({ shouldShow: true }));
        return;
      }
      // Each artist has its own checklist: the screen opens under the artist of the action. With the
      // demo artist active, an action with no artist opens under the first own artist.
      const targetId = action.accountId ?? (isDemoActive ? firstOwnAccountId(accounts) : null);
      if (targetId !== null && (isDemoActive || targetId !== active?.id)) {
        onSelect(targetId);
      }
      switch (action.screen) {
        case 'addSong':
          router.push(SMLRoutes.addSong);
          break;
        case 'songPage':
          // eslint-disable-next-line no-void
          void navigate(
            catalogSongPath(action.catalogItemUuid, { section: action.section, accountId: action.accountId })
          );
          break;
        case 'releaseTheme':
          // The Theme panel sits on the Overview tab (design releases-v2 b1). `theme=edit` opens its
          // editor at once, so the step lands on the look picker (QA 2026-10-28, mob-6).
          // eslint-disable-next-line no-void
          void navigate(releaseThemeEditPath(action.catalogItemUuid, action.accountId));
          break;
        case 'releaseAssets':
          router.push(SMLRoutes.promoteRelease, {
            catalogItemUuid: action.catalogItemUuid,
            tab: 'kit',
            accountId: action.accountId,
          });
          break;
        case 'releasePlan':
          router.push(SMLRoutes.promoteRelease, {
            catalogItemUuid: action.catalogItemUuid,
            tab: 'plan',
            accountId: action.accountId,
          });
          break;
        case 'channels':
          router.push(SMLRoutes.promoteChannels);
          break;
        case 'catalogConnect':
          router.push(SMLRoutes.catalog);
          break;
        default:
          break;
      }
    },
    [accounts, active?.id, dispatch, isDemoActive, navigate, onSelect, router]
  );
}
